import React, {
  createContext,
  useEffect,
  useContext,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useStore, useSelector } from 'react-redux';
import {
  initSplitSdk,
  getSplitNames,
  getTreatments,
  selectTreatmentValue,
} from '@splitsoftware/splitio-redux';

import {
  APP_DOMAIN,
  SPLITIO_API_KEY,
} from 'appenv';
import { UserSessionContext } from 'components/UserSession';
import { selectXtraEventCreatedAt } from 'models/cms/events';

enum SplitioFeature {
  useCachedCMSData = 'vexpo_spa_use_cached_cms_data',
  enableMyProfile = 'vexpo_spa_my_profile',
  twilioChat = 'vexpo_twilio_chat',
  enableExhibitorPanel = 'vexpo_spa_exhibitor_panel',
  enableHotjar = 'vexpo_spa_hotjar',
}
interface SplitioContextType {
  isReady: boolean;
  isTreatmentOn: (feature: SplitioFeature) => boolean;
}

const SplitioContext = createContext<SplitioContextType>({
  isReady: false,
  isTreatmentOn: (feature: SplitioFeature) => false,
});

const SplitioContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const { userSession } = useContext(UserSessionContext);
  const [isReady, setIsReady] = useState(false);
  const eventCreatedAt = useSelector(selectXtraEventCreatedAt);

  const isTreatmentOn = useCallback((feature: SplitioFeature) => {
    const treatment = selectTreatmentValue(store.getState().splitio, feature);
    return treatment === 'on';
  }, [store]);

  useEffect(() => {
    if (eventCreatedAt) {
      const splitioConfig = {
        core: {
          authorizationKey: SPLITIO_API_KEY,
          key: userSession?.email || 'anonymous_vexpo_user',
          trafficType: 'user',
        },
      };
      dispatch(initSplitSdk({ config: splitioConfig })).then(() => {
        const splitNames = getSplitNames();
        const attributes = {
          eventDomain: APP_DOMAIN,
          eventCreatedAt: new Date(eventCreatedAt).getTime(),
          role: userSession?.role || 'attendee',
        };
        dispatch(getTreatments({ splitNames, attributes }));
        setIsReady(true);
      })
        .finally(() => {
          setIsReady(true);
        });
    }
  }, [dispatch, userSession, eventCreatedAt]);

  return (
    <SplitioContext.Provider
      value={{
        isReady,
        isTreatmentOn,
      }}
    >
      {children}
    </SplitioContext.Provider>
  );
};

SplitioContextProvider.propTypes = {
  children: PropTypes.node,
};

SplitioContextProvider.defaultProps = {
  children: null,
};

export {
  SplitioContext,
  SplitioContextProvider,
  SplitioFeature,
};
